import type { RefObject } from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import type { PositionWithOffsetValue } from '@elfsight/configurator-contracts';
import { positionMixin } from './mixins';

const OFFSET = 100;
const CLOSING_CLASS = 'shadow-closing';

type ShadowProps = {
  position: PositionWithOffsetValue['position'];
  verticalOffset: PositionWithOffsetValue['verticalOffset'];
  horizontalOffset: PositionWithOffsetValue['horizontalOffset'];
  targetElement: RefObject<HTMLElement>;
  /**
   * needed to animate the shadow
   */
  open: boolean;
  animationDuration: number;
};

export function Shadow({
  position,
  verticalOffset,
  horizontalOffset,
  targetElement,
  open,
  animationDuration
}: ShadowProps) {
  const [style, setStyle] = useState({});

  useEffect(() => {
    const { current: target } = targetElement;

    if (!target) {
      return;
    }

    const targetRect = target.getBoundingClientRect();

    const width = targetRect.width + horizontalOffset + OFFSET;
    const height = targetRect.height + verticalOffset + OFFSET;

    const positionStyles = positionMixin({
      position,
      verticalOffset: -height,
      horizontalOffset: -width
    });

    setStyle({
      ...positionStyles,
      width: width * 2,
      height: height * 2
    });
  }, [targetElement, position, verticalOffset, horizontalOffset]);

  return (
    <ShadowContainer
      _animationDuration={animationDuration}
      style={style}
      className={open ? '' : CLOSING_CLASS}
    />
  );
}

const ShadowContainer = styled.div<{
  _animationDuration: number;
}>`
  position: fixed;
  pointer-events: none;
  z-index: 1;
  transition: all ${({ _animationDuration }) => _animationDuration}ms;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(17, 17, 17, 0.05) 65%,
    rgba(17, 17, 17, 0) 100%
  );

  &.${CLOSING_CLASS} {
    opacity: 0;
  }

  @starting-style {
    & {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    & {
      transition: none;
    }
  }
`;
